import { create } from 'zustand';
import {
  getAllOrganizations,
  getOrganizationById,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  toggleOrganizationStatus,
  getOrganizationUsers,
  assignUserToOrganization,
  removeUserFromOrganization,
  getUserOrganizations,
  Organization,
  OrganizationDetail,
  OrganizationUser,
  Pagination,
  CreateOrganizationDTO,
  UpdateOrganizationDTO,
} from '@/services/PagesServices/OrganizationService';

interface OrganizationState {
  // Data states
  organizations: Organization[];
  selectedOrganization: OrganizationDetail | null;
  organizationUsers: OrganizationUser[];
  userOrganizations: Organization[];

  // Pagination states
  organizationsPagination: Pagination | null;
  usersPagination: Pagination | null;

  // Filter states
  searchTerm: string;
  selectedStatus: string | boolean;
  currentPage: number;
  itemsPerPage: number;

  // Loading states
  loadingOrganizations: boolean;
  loadingOrganization: boolean;
  loadingUsers: boolean;
  loadingUserOrganizations: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  toggling: boolean;
  assigning: boolean;
  removing: boolean;

  // Error states
  error: string | null;

  // Actions
  fetchOrganizations: (params?: {
    search?: string;
    isActive?: boolean | string;
    page?: number;
    limit?: number;
  }) => Promise<void>;
  fetchOrganizationById: (organizationId: number) => Promise<OrganizationDetail | null>;
  fetchOrganizationUsers: (organizationId: number, page?: number, limit?: number) => Promise<void>;
  fetchUserOrganizations: (userId: string) => Promise<void>;
  createOrganization: (organizationData: CreateOrganizationDTO) => Promise<{ success: boolean; message: string; data?: { id: number } }>;
  updateOrganization: (organizationId: number, organizationData: UpdateOrganizationDTO) => Promise<{ success: boolean; message: string }>;
  deleteOrganization: (organizationId: number) => Promise<{ success: boolean; message: string }>;
  toggleStatus: (organizationId: number, isActive: boolean) => Promise<{ success: boolean; message: string }>;
  assignUser: (userId: string, organizationId: number) => Promise<{ success: boolean; message: string }>;
  removeUser: (userId: string, organizationId: number) => Promise<{ success: boolean; message: string }>;

  // Convenience methods for direct data access
  getOrganizationById: (organizationId: number) => Promise<OrganizationDetail | null>;
  getOrganizationUsers: (organizationId: number, params?: { page?: number; limit?: number; search?: string }) => Promise<{ success: boolean; users?: OrganizationUser[]; pagination?: Pagination }>;

  // Filter actions
  setSearchTerm: (term: string) => void;
  setSelectedStatus: (status: string | boolean) => void;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (limit: number) => void;
  setSelectedOrganization: (organization: OrganizationDetail | null) => void;
  clearFilters: () => void;
  clearError: () => void;
}

const useOrganizationStore = create<OrganizationState>((set, get) => ({
  // Initial states
  organizations: [],
  selectedOrganization: null,
  organizationUsers: [],
  userOrganizations: [],

  organizationsPagination: null,
  usersPagination: null,

  searchTerm: '',
  selectedStatus: '',
  currentPage: 1,
  itemsPerPage: 10,

  loadingOrganizations: false,
  loadingOrganization: false,
  loadingUsers: false,
  loadingUserOrganizations: false,
  creating: false,
  updating: false,
  deleting: false,
  toggling: false,
  assigning: false,
  removing: false,

  error: null,

  // Fetch organizations with filters and pagination
  fetchOrganizations: async (params) => {
    const state = get();
    const queryParams = {
      search: params?.search ?? state.searchTerm,
      isActive: params?.isActive ?? state.selectedStatus,
      page: params?.page ?? state.currentPage,
      limit: params?.limit ?? state.itemsPerPage,
    };

    set({ loadingOrganizations: true, error: null });

    try {
      const response = await getAllOrganizations(queryParams);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to fetch organizations');
      }

      set({
        organizations: response.data || [],
        organizationsPagination: response.pagination || null,
        loadingOrganizations: false,
      });
    } catch (error: any) {
      console.error('OrganizationStore: Error in fetchOrganizations:', error);
      set({
        error: error.message ?? 'Something went wrong',
        loadingOrganizations: false,
      });
    }
  },

  // Fetch single organization by ID
  fetchOrganizationById: async (organizationId) => {
    set({ loadingOrganization: true, error: null });

    try {
      const response = await getOrganizationById(organizationId);

      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Organization not found');
      }

      set({
        selectedOrganization: response.data,
        loadingOrganization: false,
      });

      return response.data;
    } catch (error: any) {
      console.error('OrganizationStore: Error in fetchOrganizationById:', error);
      set({
        error: error.message ?? 'Failed to fetch organization',
        loadingOrganization: false,
      });
      return null;
    }
  },

  // Fetch organization users
  fetchOrganizationUsers: async (organizationId, page = 1, limit = 10) => {
    set({ loadingUsers: true, error: null });

    try {
      const response = await getOrganizationUsers(organizationId, { page, limit });

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to fetch organization users');
      }

      set({
        organizationUsers: response.data || [],
        usersPagination: response.pagination || null,
        loadingUsers: false,
      });
    } catch (error: any) {
      console.error('OrganizationStore: Error in fetchOrganizationUsers:', error);
      set({
        error: error.message ?? 'Failed to fetch organization users',
        loadingUsers: false,
      });
    }
  },

  // Fetch user organizations
  fetchUserOrganizations: async (userId) => {
    set({ loadingUserOrganizations: true, error: null });

    try {
      const response = await getUserOrganizations(userId);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to fetch user organizations');
      }

      set({
        userOrganizations: response.data || [],
        loadingUserOrganizations: false,
      });
    } catch (error: any) {
      console.error('OrganizationStore: Error in fetchUserOrganizations:', error);
      set({
        error: error.message ?? 'Failed to fetch user organizations',
        loadingUserOrganizations: false,
      });
    }
  },

  // Create new organization
  createOrganization: async (organizationData) => {
    set({ creating: true, error: null });

    try {
      const response = await createOrganization(organizationData);

      set({ creating: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to create organization',
        };
      }

      // Refresh organizations list
      get().fetchOrganizations();

      return {
        success: true,
        message: response.message || 'Organization created successfully',
        data: response.data,
      };
    } catch (error: any) {
      console.error('OrganizationStore: Error in createOrganization:', error);
      set({ creating: false });

      return {
        success: false,
        message: error.message ?? 'Failed to create organization',
      };
    }
  },

  // Update organization
  updateOrganization: async (organizationId, organizationData) => {
    set({ updating: true, error: null });

    try {
      const response = await updateOrganization(organizationId, organizationData);

      set({ updating: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to update organization',
        };
      }

      // Refresh organizations list
      get().fetchOrganizations();

      return {
        success: true,
        message: response.message ?? 'Organization updated successfully',
      };
    } catch (error: any) {
      console.error('OrganizationStore: Error in updateOrganization:', error);
      set({ updating: false });

      return {
        success: false,
        message: error.message ?? 'Failed to update organization',
      };
    }
  },

  // Delete organization
  deleteOrganization: async (organizationId) => {
    set({ deleting: true, error: null });

    try {
      const response = await deleteOrganization(organizationId);

      set({ deleting: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to delete organization',
        };
      }

      // Refresh organizations list
      get().fetchOrganizations();

      return {
        success: true,
        message: response.message ?? 'Organization deleted successfully',
      };
    } catch (error: any) {
      console.error('OrganizationStore: Error in deleteOrganization:', error);
      set({ deleting: false });

      return {
        success: false,
        message: error.message ?? 'Failed to delete organization',
      };
    }
  },

  // Toggle organization status
  toggleStatus: async (organizationId, isActive) => {
    set({ toggling: true, error: null });

    try {
      const response = await toggleOrganizationStatus(organizationId, isActive);

      set({ toggling: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to toggle organization status',
        };
      }

      // Refresh organizations list
      get().fetchOrganizations();

      return {
        success: true,
        message: response.message ?? 'Organization status updated successfully',
      };
    } catch (error: any) {
      console.error('OrganizationStore: Error in toggleStatus:', error);
      set({ toggling: false });

      return {
        success: false,
        message: error.message ?? 'Failed to toggle organization status',
      };
    }
  },

  // Assign user to organization
  assignUser: async (userId, organizationId) => {
    set({ assigning: true, error: null });

    try {
      const response = await assignUserToOrganization(userId, organizationId);

      set({ assigning: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to assign user to organization',
        };
      }

      return {
        success: true,
        message: response.message ?? 'User assigned to organization successfully',
      };
    } catch (error: any) {
      console.error('OrganizationStore: Error in assignUser:', error);
      set({ assigning: false });

      return {
        success: false,
        message: error.message ?? 'Failed to assign user to organization',
      };
    }
  },

  // Remove user from organization
  removeUser: async (userId, organizationId) => {
    set({ removing: true, error: null });

    try {
      const response = await removeUserFromOrganization(userId, organizationId);

      set({ removing: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to remove user from organization',
        };
      }

      // Refresh organization users if we have a selected organization
      const selectedOrg = get().selectedOrganization;
      if (selectedOrg) {
        get().fetchOrganizationUsers(selectedOrg.id);
      }

      return {
        success: true,
        message: response.message ?? 'User removed from organization successfully',
      };
    } catch (error: any) {
      console.error('OrganizationStore: Error in removeUser:', error);
      set({ removing: false });

      return {
        success: false,
        message: error.message ?? 'Failed to remove user from organization',
      };
    }
  },

  // Convenience methods for direct data access (don't update store state)
  getOrganizationById: async (organizationId) => {
    try {
      const response = await getOrganizationById(organizationId);

      if (!response.success || !response.data) {
        return null;
      }

      return response.data;
    } catch (error: any) {
      console.error('OrganizationStore: Error in getOrganizationById:', error);
      return null;
    }
  },

  getOrganizationUsers: async (organizationId, params) => {
    try {
      const response = await getOrganizationUsers(organizationId, params || {});

      return {
        success: response.success,
        users: response.data,
        pagination: response.pagination,
      };
    } catch (error: any) {
      console.error('OrganizationStore: Error in getOrganizationUsers:', error);
      return {
        success: false,
        users: [],
      };
    }
  },

  // Filter actions
  setSearchTerm: (term) => set({ searchTerm: term }),
  setSelectedStatus: (status) => set({ selectedStatus: status }),
  setCurrentPage: (page) => set({ currentPage: page }),
  setItemsPerPage: (limit) => set({ itemsPerPage: limit }),
  setSelectedOrganization: (organization) => set({ selectedOrganization: organization }),

  clearFilters: () =>
    set({
      searchTerm: '',
      selectedStatus: '',
      currentPage: 1,
    }),

  clearError: () => set({ error: null }),
}));

export default useOrganizationStore;
